import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getEngagementDisplay } from '@/lib/engagement';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Edit, Trash2, TrendingUp, Users, Search, UserRound, Upload, Download, X, StickyNote, Save, Calendar, FileText, UserX, RotateCcw, ListFilter, UserMinus, RefreshCw, Zap, Clock, Check, CheckCircle2, XCircle, Hand, Eye, Loader2, ChevronRight, History, Home, AlertTriangle, KeyRound, Smartphone, Mail } from 'lucide-react';
import { API, calcAgeNum, formatPhone, EMAIL_REGEX, INSTRUMENTOS, fmtDate, norm } from '@/lib/helpers';
import { formatBrazilDate, formatBrazilDateTimeCompact } from '@/lib/timezone';
import { Textarea } from '@/components/ui/textarea';
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput';
import { emptyAddressFields, hasPersonAddress, parseGooglePlaceAddress } from '@/lib/address';

const BACKEND_URL = window.location.origin;

function photoFrameStyle(pessoa = {}) {
  return { objectPosition: `${Number(pessoa.foto_pos_x ?? 50)}% ${Number(pessoa.foto_pos_y ?? 50)}%`, transform: `scale(${Number(pessoa.foto_zoom ?? 1)})`, transformOrigin: `${Number(pessoa.foto_pos_x ?? 50)}% ${Number(pessoa.foto_pos_y ?? 50)}%` };
}

function formatPhotoSize(size) {
  const bytes = Number(size || 0);
  if (!bytes) return 'Tamanho não disponível';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function PessoaAvatar({ pessoa, className = 'w-8 h-8 sm:w-10 sm:h-10', fallbackClassName = '', testId = 'pessoa-avatar', preferThumb = true }) {
  const imageUrl = preferThumb ? (pessoa?.foto_thumb_url || pessoa?.foto_url) : pessoa?.foto_url;
  if (imageUrl) return <span className={`${className} rounded-full overflow-hidden border-2 border-white shadow-sm flex-shrink-0 bg-slate-100 inline-flex`} data-testid={testId}><img src={`${BACKEND_URL}${imageUrl}`} alt={`Foto de ${pessoa.nome_completo || 'pessoa'}`} className="h-full w-full object-cover" style={photoFrameStyle(pessoa)} loading="lazy" decoding="async" /></span>;
  return <div className={`${className} rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${fallbackClassName}`} style={fallbackClassName ? undefined : { backgroundColor: pessoa?.genero === 'feminino' ? '#EC4899' : '#3B82F6' }} data-testid={`${testId}-fallback`}>{(pessoa?.nome_completo || '?').charAt(0)}</div>;
}

function HistoricoTab({ pessoaId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pessoaId) return;
    setLoading(true);
    fetch(`${API}/audit-logs/pessoa/${pessoaId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [pessoaId]);

  const fmtTimestamp = (ts) => {
    return formatBrazilDateTimeCompact(ts, ts || '');
  };

  const FIELD_LABELS = {
    nome_completo: 'Nome', genero: 'Género', data_nascimento: 'Data de nascimento',
    email: 'E-mail', whatsapp: 'WhatsApp', whatsapp_pai: 'WhatsApp do Pai', whatsapp_mae: 'WhatsApp da Mãe',
    batizado: 'Batizado', selado: 'Selado', nome_pai: 'Nome do Pai', nome_mae: 'Nome da Mãe',
    status: 'Status', musico: 'Músico', organista: 'Organista', aluno_gem: 'Aluno GEM',
    auxiliar_jovens: 'Auxiliar de Jovens', grupo_visita: 'Grupo de Visita',
    instrutor_gem: 'Instrutor GEM', instrutora_musicalizacao: 'Instrutora de Musicalização',
    integrante_ebi: 'Integrante EBI', instrumentos: 'Instrumentos',
    fila_manual: 'Atribuição manual', apoio_auxiliar_id: 'Auxiliar de apoio',
  };

  const HIDDEN = new Set(['pessoa_id', 'church_id', 'created_at', 'approved_at', 'approved_by', '_id', 'fila_id', 'created_by']);

  const fmtVal = (v) => {
    if (v === null || v === undefined || v === '') return '-';
    if (v === true) return 'Sim';
    if (v === false) return 'Não';
    if (Array.isArray(v)) return v.length === 0 ? '-' : v.join(', ');
    const s = String(v);
    if (s === 'masculino') return 'Masculino';
    if (s === 'feminino') return 'Feminino';
    if (s === 'pendente') return 'Pendente';
    if (s === 'aprovado') return 'Aprovado';
    if (s === 'rejeitado') return 'Rejeitado';
    return s;
  };

  const actionColors = {
    pessoa_created: 'bg-green-100 text-green-700',
    public_registration: 'bg-green-100 text-green-700',
    pessoa_approved: 'bg-blue-100 text-blue-700',
    pessoa_rejected: 'bg-red-100 text-red-700',
    pessoa_updated: 'bg-amber-100 text-amber-700',
    pessoa_updated_by_auxiliar: 'bg-amber-100 text-amber-700',
    pending_update_approved: 'bg-blue-100 text-blue-700',
    pessoa_deleted: 'bg-red-100 text-red-700',
    pessoa_status_changed: 'bg-purple-100 text-purple-700',
    pessoa_fila_updated: 'bg-cyan-100 text-cyan-700',
    bulk_assign_fila: 'bg-cyan-100 text-cyan-700',
    migracao_fila: 'bg-teal-100 text-teal-700',
    presenca_registrada: 'bg-emerald-100 text-emerald-700',
  };

  const actionIcons = {
    pessoa_created: <CheckCircle2 className="w-3.5 h-3.5" />,
    public_registration: <UserRound className="w-3.5 h-3.5" />,
    pessoa_approved: <Check className="w-3.5 h-3.5" />,
    pessoa_rejected: <XCircle className="w-3.5 h-3.5" />,
    pessoa_updated: <Edit className="w-3.5 h-3.5" />,
    pessoa_updated_by_auxiliar: <Edit className="w-3.5 h-3.5" />,
    pending_update_approved: <Check className="w-3.5 h-3.5" />,
    pessoa_deleted: <Trash2 className="w-3.5 h-3.5" />,
    pessoa_status_changed: <RefreshCw className="w-3.5 h-3.5" />,
    pessoa_fila_updated: <Users className="w-3.5 h-3.5" />,
    bulk_assign_fila: <Users className="w-3.5 h-3.5" />,
    migracao_fila: <TrendingUp className="w-3.5 h-3.5" />,
    presenca_registrada: <Calendar className="w-3.5 h-3.5" />,
  };

  const isUpdateAction = (action) => action && (action.includes('updated') || action === 'pending_update_approved');

  if (loading) return (
    <div className="text-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
      <p className="text-sm text-slate-500 mt-2">Carregando histórico...</p>
    </div>
  );

  if (!data || data.length === 0) return (
    <div className="text-center py-12">
      <History className="w-10 h-10 text-slate-300 mx-auto mb-2" />
      <p className="text-sm text-slate-500">Nenhum histórico registrado</p>
    </div>
  );

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Histórico</h3>
      <p className="text-sm text-slate-500">Todas as ações registradas desde o cadastro.</p>
      <div className="relative" data-testid="pessoa-historico-list">
        <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200"></div>
        {data.map((entry, i) => {
          const colorClass = actionColors[entry.action] || 'bg-slate-100 text-slate-600';
          const icon = actionIcons[entry.action] || <Clock className="w-3.5 h-3.5" />;
          const showDiff = isUpdateAction(entry.action) && entry.dados_antes && entry.dados_depois;
          const changedFields = showDiff
            ? Object.keys(entry.dados_depois).filter(k =>
                !HIDDEN.has(k) && JSON.stringify(entry.dados_antes[k]) !== JSON.stringify(entry.dados_depois[k])
              )
            : [];
          return (
            <div key={i} className="relative pl-10 pb-4 last:pb-0">
              <div className={`absolute left-2 top-1 w-5 h-5 rounded-full flex items-center justify-center ${colorClass}`}>
                {icon}
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <p className="text-sm font-medium text-slate-900">{entry.action_label}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-500">{fmtTimestamp(entry.timestamp)}</span>
                  {entry.user_name && <span className="text-xs text-slate-400">por {entry.user_name}</span>}
                </div>
                {changedFields.length > 0 && (
                  <div className="mt-1.5 space-y-0.5">
                    {changedFields.map(k => (
                      <p key={k} className="text-xs text-slate-600">
                        <span className="font-medium text-slate-700">{FIELD_LABELS[k] || k}:</span>{' '}
                        <span className="text-red-500 line-through">{fmtVal(entry.dados_antes[k])}</span>{' '}
                        &rarr; <span className="text-green-600 font-medium">{fmtVal(entry.dados_depois[k])}</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EMPTY_FORM = {
  nome_completo: '', apelido: '', genero: '', data_nascimento: '', email: '', whatsapp: '', whatsapp_pai: '', whatsapp_mae: '',
  foto_url: '',
  foto_thumb_url: '',
  foto_size: null,
  foto_content_type: '',
  foto_thumb_size: null,
  foto_thumb_content_type: '',
  foto_pos_x: 50,
  foto_pos_y: 50,
  foto_zoom: 1,
  batizado: false, selado: false, nome_pai: '', nome_mae: '',
  ...emptyAddressFields(),
  auxiliar_jovens: false, grupo_visita: false,
  usuario_sistema_ativo: false, system_role: 'auxiliar',
  organista: false, instrutora_musicalizacao: false, integrante_ebi: false,
  musico: false, instrumentos: [], instrutor_gem: false, aluno_gem: false,
};

function personAddressFields(source = {}) {
  return { ...emptyAddressFields(), endereco: source.endereco || source.endereco_formatado || '', endereco_formatado: source.endereco_formatado || source.endereco || '', logradouro: source.logradouro || '', numero: source.numero || '', bairro: source.bairro || '', cidade: source.cidade || '', uf: source.uf || '', cep: source.cep || '', complemento: source.complemento || '', lat: source.lat ?? null, lng: source.lng ?? null, google_place_id: source.google_place_id || '' };
}

function PersonAddressFields({ form, setForm, prefix = 'pessoa' }) {
  const handleAddressChange = (value, place) => {
    if (!place) setForm(prev => ({ ...prev, endereco: value, endereco_formatado: value, google_place_id: '', lat: null, lng: null }));
    else setForm(prev => ({ ...prev, ...parseGooglePlaceAddress(place, value) }));
  };
  return <div className="col-span-1 sm:col-span-2 grid grid-cols-1 gap-3" data-testid={`${prefix}-address-section`}>
    <div><Label>Endereço</Label><AddressAutocompleteInput value={form.endereco_formatado || form.endereco || ''} onChange={handleAddressChange} placeholder="Buscar endereço..." testId={`${prefix}-address-input`} /></div>
    <div><Label>Complemento</Label><Input value={form.complemento || ''} onChange={(e) => setForm(prev => ({ ...prev, complemento: e.target.value }))} placeholder="Casa, bloco, apto..." data-testid={`${prefix}-address-complement-input`} /></div>
  </div>;
}

function PhotoFrameControls({ form, setForm, onAutoSave, prefix = 'pessoa' }) {
  const update = (field, value) => {
    const next = { ...form, [field]: Number(value) };
    setForm(next);
    onAutoSave?.(next);
  };
  return <div className="mt-2 rounded-lg border border-slate-200 bg-white/80 p-2" data-testid={`${prefix}-photo-frame-controls`}>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <label className="space-y-0.5"><span className="text-[10px] font-bold uppercase text-slate-500">Horizontal</span><Input className="h-6 px-0" type="range" min="0" max="100" value={form.foto_pos_x ?? 50} onChange={e => update('foto_pos_x', e.target.value)} data-testid={`${prefix}-photo-pos-x`} /></label>
      <label className="space-y-0.5"><span className="text-[10px] font-bold uppercase text-slate-500">Vertical</span><Input className="h-6 px-0" type="range" min="0" max="100" value={form.foto_pos_y ?? 50} onChange={e => update('foto_pos_y', e.target.value)} data-testid={`${prefix}-photo-pos-y`} /></label>
      <label className="space-y-0.5"><span className="text-[10px] font-bold uppercase text-slate-500">Zoom</span><Input className="h-6 px-0" type="range" min="1" max="2" step="0.05" value={form.foto_zoom ?? 1} onChange={e => update('foto_zoom', e.target.value)} data-testid={`${prefix}-photo-zoom`} /></label>
    </div>
  </div>;
}

const PEOPLE_RENDER_BATCH = 120;

export default function PessoasTab({ pessoasStats, pessoas, auxiliares, loading = false, onRefresh, onDelete, motivos = [], fetchMotivos, pessoasPendentes = [], pessoasRejeitadas = [], onApprove, onReject, pendingEditPessoaId, onClearPendingEdit, pendingUpdates = [], onApproveUpdate, onRejectUpdate, engajamentoDisplay = 'niveis' }) {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [selected, setSelected] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [editTab, setEditTab] = useState('info');
  const [filter, setFilter] = useState(pessoasPendentes.length > 0 ? 'pendentes' : 'todos');
  const [filterGenero, setFilterGenero] = useState('');
  const [filterIdadeMin, setFilterIdadeMin] = useState('');
  const [filterIdadeMax, setFilterIdadeMax] = useState('');
  const [filterInfos, setFilterInfos] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(PEOPLE_RENDER_BATCH);
  const [pendentesSubFilter, setPendentesSubFilter] = useState('pendente');
  const autoOpenedPendentesRef = useRef(false);

  useEffect(() => {
    if (pessoasPendentes.length > 0 && !autoOpenedPendentesRef.current) {
      autoOpenedPendentesRef.current = true;
      setFilter(prev => prev === 'todos' ? 'pendentes' : prev);
    }
  }, [pessoasPendentes.length]);

  useEffect(() => { setVisibleLimit(PEOPLE_RENDER_BATCH); }, [filter, search, filterGenero, filterIdadeMin, filterIdadeMax, filterInfos.length, pendentesSubFilter]);

  // Inativacao state
  const [inativarDialog, setInativarDialog] = useState(false);
  const [inativarPessoa, setInativarPessoa] = useState(null);
  const [inativarMotivo, setInativarMotivo] = useState('');
  const [inativos, setInativos] = useState([]);
  const [inativosLoading, setInativosLoading] = useState(false);

  // Visita creation state
  const [visitaOpen, setVisitaOpen] = useState(false);
  const [visitaForm, setVisitaForm] = useState({ nome: '', telefone: '', observacao: '' });
  const [visitaLoading, setVisitaLoading] = useState(false);
  const [visitaMatched, setVisitaMatched] = useState(null);
  const [visitaPhoneChecked, setVisitaPhoneChecked] = useState(false);
  const [visitaPhoneChecking, setVisitaPhoneChecking] = useState(false);
  const visitaDebounceRef = useRef(null);

  // Anotacoes state
  const [anotacoes, setAnotacoes] = useState([]);
  const [anotLoading, setAnotLoading] = useState(false);
  const [anotDialog, setAnotDialog] = useState(false);
  const [editingAnot, setEditingAnot] = useState(null);
  const [anotForm, setAnotForm] = useState({ titulo: '', descricao: '', data: '' });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoOverrides, setPhotoOverrides] = useState({});
  const photoFrameSaveTimerRef = useRef(null);
  const [sendingPasswordId, setSendingPasswordId] = useState(null);

  // Service functions for "pessoas sob cuidado"
  const [serviceFunctions, setServiceFunctions] = useState([]);
  const [quickViewPessoa, setQuickViewPessoa] = useState(null);
  const fetchServiceFunctions = useCallback(() => {
    fetch(`${API}/service-functions`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setServiceFunctions)
      .catch(() => {});
  }, []);

  const fetchAnotacoes = useCallback(async (pessoaId) => {
    setAnotLoading(true);
    try {
      const r = await fetch(`${API}/pessoas/${pessoaId}/anotacoes`, { credentials: 'include' });
      if (r.ok) setAnotacoes(await r.json());
    } catch (err) { console.error(err); }
    finally { setAnotLoading(false); }
  }, []);

  const resetAnotForm = () => { setAnotForm({ titulo: '', descricao: '', data: '' }); setEditingAnot(null); };

  const handleSaveAnot = async () => {
    if (!anotForm.descricao.trim()) { toast.error('Descrição é obrigatória'); return; }
    setSaving(true);
    try {
      const pid = editing.pessoa_id;
      const url = editingAnot ? `${API}/pessoas/${pid}/anotacoes/${editingAnot.anotacao_id}` : `${API}/pessoas/${pid}/anotacoes`;
      const r = await fetch(url, { method: editingAnot ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ titulo: anotForm.data ? fmtDate(anotForm.data) : 'Anotação', descricao: anotForm.descricao, data: anotForm.data || null }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success(editingAnot ? 'Anotação atualizada!' : 'Anotação adicionada!');
      setAnotDialog(false); resetAnotForm(); fetchAnotacoes(pid);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleDeleteAnot = async (anotacaoId) => {
    try {
      const pid = editing.pessoa_id;
      const r = await fetch(`${API}/pessoas/${pid}/anotacoes/${anotacaoId}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success('Anotação excluída!'); fetchAnotacoes(pid);
    } catch (err) { toast.error(err.message); }
  };

  const fmtDate = (d) => formatBrazilDate(d, d || '');

  const toggleSelect = (id) => {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(p => p.pessoa_id)));
  };

  const clearSelection = () => { setSelected(new Set()); };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) { toast.error('Envie um arquivo .xlsx'); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await fetch(`${API}/pessoas/import`, { method: 'POST', credentials: 'include', body: formData });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail || 'Erro na importação'); }
      const data = await r.json();
      if (data.errors?.length > 0) toast.warning(`${data.imported} importados, ${data.errors.length} erros`, { duration: 6000, description: data.errors.slice(0, 3).join('\n') });
      else toast.success(`${data.imported} pessoas importadas com sucesso!`);
      onRefresh();
    } catch (err) { toast.error(err.message); } finally { setImporting(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Envie uma foto JPG, PNG ou WebP.'); e.target.value = ''; return; }
    if (file.size > 3 * 1024 * 1024) { toast.error('Foto muito grande. Limite máximo: 3MB.'); e.target.value = ''; return; }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await fetch(`${API}/upload/photo`, { method: 'POST', credentials: 'include', body: formData });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao enviar foto');
      const nextForm = { ...form, foto_url: data.url || '', foto_thumb_url: data.thumb_url || data.url || '', foto_size: data.size || null, foto_content_type: data.content_type || '', foto_thumb_size: data.thumb_size || null, foto_thumb_content_type: data.thumb_content_type || '', foto_pos_x: 50, foto_pos_y: 50, foto_zoom: 1 };
      setForm(nextForm);
      if (editing?.pessoa_id) await saveExistingPhotoFields(nextForm, 'Foto enviada e salva automaticamente.');
      else toast.success('Foto enviada. Salve o cadastro para concluir.');
    } catch (err) { toast.error(err.message); }
    finally { setUploadingPhoto(false); e.target.value = ''; }
  };

  const handleRemovePhoto = async () => {
    const nextForm = { ...form, foto_url: '', foto_thumb_url: '', foto_size: null, foto_content_type: '', foto_thumb_size: null, foto_thumb_content_type: '', foto_pos_x: 50, foto_pos_y: 50, foto_zoom: 1 };
    setForm(nextForm);
    if (!editing?.pessoa_id) return;
    setUploadingPhoto(true);
    try { await saveExistingPhotoFields(nextForm, 'Foto removida.'); }
    catch (err) { toast.error(err.message); }
    finally { setUploadingPhoto(false); }
  };

  const schedulePhotoFrameAutoSave = (nextForm) => {
    if (!editing?.pessoa_id) return;
    setPhotoOverrides(prev => ({ ...prev, [editing.pessoa_id]: { foto_url: nextForm.foto_url || '', foto_thumb_url: nextForm.foto_thumb_url || '', foto_size: nextForm.foto_size || null, foto_content_type: nextForm.foto_content_type || '', foto_thumb_size: nextForm.foto_thumb_size || null, foto_thumb_content_type: nextForm.foto_thumb_content_type || '', foto_pos_x: nextForm.foto_pos_x ?? 50, foto_pos_y: nextForm.foto_pos_y ?? 50, foto_zoom: nextForm.foto_zoom ?? 1 } }));
    if (photoFrameSaveTimerRef.current) clearTimeout(photoFrameSaveTimerRef.current);
    photoFrameSaveTimerRef.current = setTimeout(async () => {
      try { await saveExistingPhotoFields(nextForm, '', false); }
      catch (err) { toast.error(err.message); }
    }, 700);
  };

  const [templateDialog, setTemplateDialog] = useState(false);
  const [birthdayCalendarDialog, setBirthdayCalendarDialog] = useState(false);
  const [birthdayCalendarUrl, setBirthdayCalendarUrl] = useState('');
  const [birthdayCalendarLoading, setBirthdayCalendarLoading] = useState(false);

  const downloadTemplate = () => {
    const a = document.createElement('a');
    a.href = `${API}/pessoas/import-template`;
    a.download = 'modelo_importacao.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadContactsVcf = () => {
    const a = document.createElement('a');
    a.href = `${API}/pessoas/contacts-vcf`;
    a.download = 'contatos.vcf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openBirthdayCalendarDialog = async () => {
    setBirthdayCalendarDialog(true);
    setBirthdayCalendarLoading(true);
    try {
      const response = await fetch(`${API}/pessoas/birthday-calendar-link`, { credentials: 'include' });
      if (!response.ok) throw new Error('Não foi possível gerar o link da agenda.');
      const data = await response.json();
      setBirthdayCalendarUrl(data.url || '');
    } catch (error) {
      toast.error(error.message || 'Erro ao gerar link da agenda.');
    } finally {
      setBirthdayCalendarLoading(false);
    }
  };

  const copyBirthdayCalendarUrl = async () => {
    try {
      await navigator.clipboard.writeText(birthdayCalendarUrl);
      toast.success('Link da agenda copiado!');
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const validate = () => {
    const e = {};
    if (!form.nome_completo.trim()) e.nome_completo = 'Obrigatório';
    if (!form.genero) e.genero = 'Obrigatório';
    if (!form.data_nascimento) e.data_nascimento = 'Obrigatório';
    if (!form.nome_pai.trim()) e.nome_pai = 'Obrigatório';
    if (!form.nome_mae.trim()) e.nome_mae = 'Obrigatório';
    if (form.email && !EMAIL_REGEX.test(form.email)) e.email = 'Email inválido';
    if (form.whatsapp && form.whatsapp.replace(/\D/g, '').length < 11) e.whatsapp = 'Número incompleto';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPessoaPayload = (source = form) => {
    const payload = { ...source };
    if (!payload.email) payload.email = null;
    if (!payload.whatsapp) payload.whatsapp = null;
    if (!payload.whatsapp_pai) payload.whatsapp_pai = null;
    if (!payload.whatsapp_mae) payload.whatsapp_mae = null;
    payload.foto_pos_x = Number(payload.foto_pos_x ?? 50);
    payload.foto_pos_y = Number(payload.foto_pos_y ?? 50);
    payload.foto_zoom = Number(payload.foto_zoom ?? 1);
    payload.foto_size = payload.foto_url ? (payload.foto_size || null) : null;
    payload.foto_content_type = payload.foto_url ? (payload.foto_content_type || null) : null;
    payload.foto_thumb_url = payload.foto_url ? (payload.foto_thumb_url || payload.foto_url || '') : '';
    payload.foto_thumb_size = payload.foto_url ? (payload.foto_thumb_size || null) : null;
    payload.foto_thumb_content_type = payload.foto_url ? (payload.foto_thumb_content_type || null) : null;
    if (payload.genero !== 'masculino') { payload.musico = false; payload.instrumentos = []; payload.instrutor_gem = false; payload.aluno_gem = false; }
    if (payload.genero !== 'feminino') { payload.organista = false; payload.instrutora_musicalizacao = false; payload.integrante_ebi = false; }
    if (!payload.musico) payload.instrumentos = [];
    return payload;
  };

  const withPhotoOverride = useCallback((pessoa) => {
    if (!pessoa?.pessoa_id || !photoOverrides[pessoa.pessoa_id]) return pessoa;
    return { ...pessoa, ...photoOverrides[pessoa.pessoa_id] };
  }, [photoOverrides]);

  const saveExistingPhotoFields = async (nextForm, successMessage = 'Foto salva automaticamente.', refresh = true) => {
    if (!editing?.pessoa_id) return false;
    const r = await fetch(`${API}/pessoas/${editing.pessoa_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(buildPessoaPayload(nextForm)) });
    const saved = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(saved.detail || 'Erro ao salvar foto');
    setEditing(saved);
    setPhotoOverrides(prev => ({ ...prev, [editing.pessoa_id]: { foto_url: saved.foto_url || '', foto_thumb_url: saved.foto_thumb_url || saved.foto_url || '', foto_size: saved.foto_size || null, foto_content_type: saved.foto_content_type || '', foto_thumb_size: saved.foto_thumb_size || null, foto_thumb_content_type: saved.foto_thumb_content_type || '', foto_pos_x: saved.foto_pos_x ?? 50, foto_pos_y: saved.foto_pos_y ?? 50, foto_zoom: saved.foto_zoom ?? 1 } }));
    if (successMessage) toast.success(successMessage);
    if (refresh) onRefresh();
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const url = editing ? `${API}/pessoas/${editing.pessoa_id}` : `${API}/pessoas`;
      const payload = buildPessoaPayload();
      const r = await fetch(url, { method: editing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      const saved = await r.json().catch(() => ({}));
      if (!r.ok) { throw new Error(saved.detail || 'Erro'); }
      if (saved.duplicate_whatsapp_warning?.message) {
        toast.warning(saved.duplicate_whatsapp_warning.message);
      } else {
        toast.success(editing ? 'Pessoa atualizada!' : 'Pessoa adicionada!');
      }
      closeDialog(); onRefresh();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setErrors({}); setEditTab('info'); setAnotacoes([]); setDialog(true); };

  const handleImpersonate = async (p) => {
    try {
      const r = await fetch(`${API}/auxiliares/${p.pessoa_id}/impersonate`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { const d = await r.json(); toast.error(d.detail || 'Erro ao impersonar'); return; }
      // Backend sets cookies via Set-Cookie header, just navigate
      navigate('/auxiliar');
      window.location.reload();
    } catch { toast.error('Erro ao impersonar'); }
  };

  const openEdit = (p) => {
    const source = withPhotoOverride(pessoas.find(x => x.pessoa_id === p.pessoa_id) || p);
    setEditing(source);
    setForm({
      nome_completo: source.nome_completo, apelido: source.apelido || '', genero: source.genero, data_nascimento: source.data_nascimento,
      email: source.email || '', whatsapp: source.whatsapp || '', whatsapp_pai: source.whatsapp_pai || '', whatsapp_mae: source.whatsapp_mae || '', foto_url: source.foto_url || '', foto_thumb_url: source.foto_thumb_url || source.foto_url || '', foto_size: source.foto_size || null, foto_content_type: source.foto_content_type || '', foto_thumb_size: source.foto_thumb_size || null, foto_thumb_content_type: source.foto_thumb_content_type || '', foto_pos_x: source.foto_pos_x ?? 50, foto_pos_y: source.foto_pos_y ?? 50, foto_zoom: source.foto_zoom ?? 1, batizado: source.batizado, selado: source.selado,
      nome_pai: source.nome_pai || '', nome_mae: source.nome_mae || '',
      ...personAddressFields(source),
      auxiliar_jovens: source.auxiliar_jovens || false, grupo_visita: source.grupo_visita || false,
      usuario_sistema_ativo: source.usuario_sistema_ativo || false, system_role: source.system_role || 'auxiliar',
      organista: source.organista || false, instrutora_musicalizacao: source.instrutora_musicalizacao || false,
      integrante_ebi: source.integrante_ebi || false, musico: source.musico || false,
      instrumentos: source.instrumentos || [], instrutor_gem: source.instrutor_gem || false,
      aluno_gem: source.aluno_gem || false,
    });
    setErrors({});
    setEditTab('info');
    fetchAnotacoes(source.pessoa_id);
    if (source.auxiliar_jovens) fetchServiceFunctions();
    setDialog(true);
  };

  const closeDialog = () => { setDialog(false); setEditing(null); setErrors({}); setAnotacoes([]); };

  const handleToggleSystemUser = async (ativo, pessoaOverride = null) => {
    const target = pessoaOverride || editing;
    if (!target) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/pessoas/${target.pessoa_id}/system-user`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ativo, role: target.system_role || form.system_role || 'auxiliar' }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao atualizar usuário do sistema');
      if (!pessoaOverride || editing?.pessoa_id === target.pessoa_id) {
        setForm(prev => ({ ...prev, usuario_sistema_ativo: ativo, system_role: data.system_role || prev.system_role }));
        setEditing(prev => prev ? ({ ...prev, usuario_sistema_ativo: ativo, system_role: data.system_role || prev.system_role }) : prev);
      }
      toast.success(ativo ? 'Usuário do sistema ativado' : 'Usuário do sistema inativado');
      onRefresh();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleSendTempPassword = async (pessoaId = editing?.pessoa_id) => {
    if (!pessoaId) return;
    setSendingPasswordId(pessoaId);
    try {
      const r = await fetch(`${API}/pessoas/${pessoaId}/send-temp-password`, { method: 'POST', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Erro ao enviar senha temporária');
      toast.success('Senha temporária enviada por SMS');
      onRefresh();
    } catch (err) { toast.error(err.message); } finally { setSendingPasswordId(null); }
  };

  useEffect(() => {
    if (pendingEditPessoaId && pessoas.length > 0) {
      const p = pessoas.find(x => x.pessoa_id === pendingEditPessoaId);
      if (p) openEdit(p);
      if (onClearPendingEdit) onClearPendingEdit();
    }
  }, [pendingEditPessoaId, pessoas]);

  const openInativar = (p) => { setInativarPessoa(p); setInativarMotivo(''); setInativarDialog(true); if (fetchMotivos) fetchMotivos(); };

  // Visita creation functions
  const visitaCheckPessoa = useCallback(async (phone) => {
    const clean = phone.replace(/\D/g, '');
    if (clean.length < 10) { setVisitaMatched(null); setVisitaPhoneChecked(false); return; }
    setVisitaPhoneChecking(true);
    try {
      const r = await fetch(`${API}/visitas/check-pessoa?telefone=${encodeURIComponent(phone)}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        if (data.found) { setVisitaMatched(data); setVisitaForm(prev => ({ ...prev, nome: data.nome_completo })); }
        else { setVisitaMatched(null); }
        setVisitaPhoneChecked(true);
      } else { setVisitaMatched(null); setVisitaPhoneChecked(true); }
    } catch { setVisitaMatched(null); setVisitaPhoneChecked(true); }
    finally { setVisitaPhoneChecking(false); }
  }, []);

  const visitaHandlePhoneChange = (val) => {
    const formatted = formatPhone(val);
    setVisitaForm(prev => ({ ...prev, telefone: formatted, nome: '' }));
    setVisitaMatched(null); setVisitaPhoneChecked(false);
    if (visitaDebounceRef.current) clearTimeout(visitaDebounceRef.current);
    visitaDebounceRef.current = setTimeout(() => visitaCheckPessoa(formatted), 400);
  };

  const openVisitaFromPessoa = (p) => {
    const phone = p.whatsapp || p.whatsapp_pai || p.whatsapp_mae || '';
    setVisitaForm({ nome: '', telefone: phone, observacao: '' });
    setVisitaMatched(null); setVisitaPhoneChecked(false); setVisitaPhoneChecking(false);
    setVisitaOpen(true);
    if (phone) setTimeout(() => visitaCheckPessoa(phone), 100);
  };

  const handleCreateVisita = async (e) => {
    e.preventDefault();
    const pid = visitaMatched?.pessoas ? visitaMatched.pessoas.map(p => p.pessoa_id) : [];
    const pnomes = visitaMatched?.pessoas ? visitaMatched.pessoas.map(p => p.nome_completo) : [];
    let nome;
    if (pnomes.length > 0) {
      const primeiros = pnomes.map(n => n.split(' ')[0]);
      nome = primeiros.length === 1 ? primeiros[0] : primeiros.slice(0, -1).join(', ') + ' e ' + primeiros[primeiros.length - 1];
    } else { nome = visitaForm.nome.trim(); }
    if (!nome || !visitaForm.telefone.trim()) { toast.error('Nome e telefone são obrigatórios'); return; }
    setVisitaLoading(true);
    try {
      const r = await fetch(`${API}/visitas`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ ...visitaForm, nome, pessoa_ids: pid, pessoas_nomes: pnomes }) });
      if (!r.ok) throw new Error('Erro');
      toast.success('Solicitação de visita cadastrada');
      setVisitaOpen(false); setVisitaForm({ nome: '', telefone: '', observacao: '' }); setVisitaMatched(null); setVisitaPhoneChecked(false);
      onRefresh();
    } catch { toast.error('Erro ao cadastrar visita'); }
    finally { setVisitaLoading(false); }
  };

  const handleInativar = async () => {
    if (!inativarMotivo.trim()) { toast.error('Informe o motivo da inativacao'); return; }
    try {
      const r = await fetch(`${API}/pessoas/${inativarPessoa.pessoa_id}/inativar`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ motivo: inativarMotivo }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success(`${inativarPessoa.nome_completo} foi inativado(a)`);
      setInativarDialog(false); setInativarPessoa(null); onRefresh();
      fetchInativos();
    } catch (err) { toast.error(err.message); }
  };

  const handleReativar = async (pessoaId) => {
    try {
      const r = await fetch(`${API}/pessoas/${pessoaId}/reativar`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success('Pessoa reativada!'); onRefresh(); fetchInativos();
    } catch (err) { toast.error(err.message); }
  };

  const fetchInativos = async () => {
    setInativosLoading(true);
    try {
      const r = await fetch(`${API}/pessoas/inativos`, { credentials: 'include' });
      if (r.ok) setInativos(await r.json());
    } catch (err) { console.error(err); }
    finally { setInativosLoading(false); }
  };

  useEffect(() => { fetchInativos(); }, []);

  const openAddAnot = (p) => {
    const source = pessoas.find(x => x.pessoa_id === p.pessoa_id) || p;
    setEditing(source);
    setForm({
      nome_completo: source.nome_completo, apelido: source.apelido || '', genero: source.genero, data_nascimento: source.data_nascimento,
      email: source.email || '', whatsapp: source.whatsapp || '', whatsapp_pai: source.whatsapp_pai || '', whatsapp_mae: source.whatsapp_mae || '', foto_url: source.foto_url || '', foto_thumb_url: source.foto_thumb_url || source.foto_url || '', foto_size: source.foto_size || null, foto_content_type: source.foto_content_type || '', foto_thumb_size: source.foto_thumb_size || null, foto_thumb_content_type: source.foto_thumb_content_type || '', foto_pos_x: source.foto_pos_x ?? 50, foto_pos_y: source.foto_pos_y ?? 50, foto_zoom: source.foto_zoom ?? 1, batizado: source.batizado, selado: source.selado,
      nome_pai: source.nome_pai || '', nome_mae: source.nome_mae || '',
      ...personAddressFields(source),
      auxiliar_jovens: source.auxiliar_jovens || false, grupo_visita: source.grupo_visita || false,
      organista: source.organista || false, instrutora_musicalizacao: source.instrutora_musicalizacao || false,
      integrante_ebi: source.integrante_ebi || false, musico: source.musico || false,
      instrumentos: source.instrumentos || [], instrutor_gem: source.instrutor_gem || false,
      aluno_gem: source.aluno_gem || false,
    });
    setErrors({});
    setEditTab('anotacoes');
    fetchAnotacoes(source.pessoa_id);
    setDialog(true);
    setTimeout(() => { resetAnotForm(); setAnotDialog(true); }, 300);
  };

  const toggleInstrumento = (inst) => setForm(p => ({
    ...p, instrumentos: p.instrumentos.includes(inst) ? p.instrumentos.filter(i => i !== inst) : [...p.instrumentos, inst]
  }));

  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeletePessoa = (pessoaId, nome) => {
    setDeleteTarget({ pessoaId, nome });
    setDeleteConfirmDialog(true);
  };

  const confirmDeletePessoa = async () => {
    if (!deleteTarget) return;
    try {
      const r = await fetch(`${API}/pessoas/${deleteTarget.pessoaId}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Erro'); }
      toast.success('Pessoa excluída!');
      fetchInativos();
      onRefresh();
    } catch (err) { toast.error(err.message); }
    setDeleteConfirmDialog(false);
    setDeleteTarget(null);
  };

  // Search filter
  const calcAge = (dob) => calcAgeNum(dob);
  const normalizedSearch = norm(search);
  const searched = useMemo(() => normalizedSearch ? pessoasStats.filter(p => norm(p.nome_completo).includes(normalizedSearch)) : pessoasStats, [pessoasStats, normalizedSearch]);

  // Build counts for sidebar (from all pessoasStats, pre-search)
  const allCounts = useMemo(() => ({ todos: pessoasStats.length, usuarios_sistema: pessoasStats.filter(p => p.usuario_sistema_ativo).length, inativos: inativos.length, pendentes: pessoasPendentes.length + pendingUpdates.length, incompletos: pessoasStats.filter(p => !p.data_nascimento).length }), [pessoasStats, inativos.length, pessoasPendentes.length, pendingUpdates.length]);

  // Apply filter (inativos/pendentes handled separately in render)
  const filteredByFila = useMemo(() => (filter === 'inativos' || filter === 'pendentes' || filter === 'incompletos' || filter === 'usuarios_sistema') ? [] : searched, [filter, searched]);

  // Apply gender and age filters
  const filtered = useMemo(() => filteredByFila.filter(p => {
    if (filterGenero && p.genero !== filterGenero) return false;
    const age = calcAge(p.data_nascimento);
    if (filterIdadeMin !== '' && (age === null || age < Number(filterIdadeMin))) return false;
    if (filterIdadeMax !== '' && (age === null || age > Number(filterIdadeMax))) return false;
    if (filterInfos.length > 0 && !filterInfos.every(f => !!p[f])) return false;
    return true;
  }), [filteredByFila, filterGenero, filterIdadeMin, filterIdadeMax, filterInfos]);

  // Search within pendentes sub-sections
  const pendentesSubSource = useMemo(() => pendentesSubFilter === 'pendente' ? pessoasPendentes : pendentesSubFilter === 'aprovado' ? pessoas : pendentesSubFilter === 'atualizacoes' ? [] : pessoasRejeitadas, [pendentesSubFilter, pessoasPendentes, pessoas, pessoasRejeitadas]);
  const filteredPendentes = useMemo(() => normalizedSearch ? pendentesSubSource.filter(p => norm(p.nome_completo).includes(normalizedSearch)) : pendentesSubSource, [pendentesSubSource, normalizedSearch]);
  const filteredUpdates = useMemo(() => pendentesSubFilter === 'atualizacoes' ? (normalizedSearch ? pendingUpdates.filter(u => norm(u.dados_novos?.nome_completo || '').includes(normalizedSearch)) : pendingUpdates) : [], [pendentesSubFilter, pendingUpdates, normalizedSearch]);
  const pendentesSubLabel = pendentesSubFilter === 'pendente' ? 'Pendentes' : pendentesSubFilter === 'aprovado' ? 'Aprovados' : pendentesSubFilter === 'atualizacoes' ? 'Atualizações' : 'Reprovados';

  // Update detail modal
  const [updateDetailModal, setUpdateDetailModal] = useState(null);

  const activeFilterLabel = filter === 'todos' ? 'Todos' : filter === 'usuarios_sistema' ? 'Usuários' : filter === 'inativos' ? 'Inativos' : filter === 'pendentes' ? pendentesSubLabel : filter === 'incompletos' ? 'Incompletos' : 'Todos';

  // Incompletos: pessoas without data_nascimento
  const incompletos = useMemo(() => normalizedSearch ? pessoasStats.filter(p => !p.data_nascimento && norm(p.nome_completo).includes(normalizedSearch)) : pessoasStats.filter(p => !p.data_nascimento), [pessoasStats, normalizedSearch]);
  const usuariosSistema = searched;
  const visibleFiltered = filtered.slice(0, visibleLimit);
  const visibleUsuariosSistema = usuariosSistema.slice(0, visibleLimit);
  const visibleIncompletos = incompletos.slice(0, visibleLimit);
  const visiblePendentes = filteredPendentes.slice(0, visibleLimit);
  const visibleUpdates = filteredUpdates.slice(0, visibleLimit);
  const visibleInativos = inativos.slice(0, visibleLimit);
  const LoadMoreButton = ({ total, testId }) => total > visibleLimit ? <div className="flex justify-center py-4" data-testid={`${testId}-load-more-wrap`}><Button variant="outline" size="sm" onClick={() => setVisibleLimit(prev => prev + PEOPLE_RENDER_BATCH)} data-testid={`${testId}-load-more-button`}>Mostrar mais {Math.min(PEOPLE_RENDER_BATCH, total - visibleLimit)} de {total}</Button></div> : null;

  // Confirm action state for pendentes
  const [confirmAction, setConfirmAction] = useState(null);

  // Info modal state for pendentes detail view
  const [infoPessoa, setInfoPessoa] = useState(null);

  const renderPessoa = (person) => {
    const p = withPhotoOverride(person);
    return <div key={p.pessoa_id} className="px-3 sm:px-4 py-3 border-b last:border-b-0 border-slate-100" data-testid={`pessoa-${p.pessoa_id}`}>
      <div className="flex items-center gap-2 sm:gap-3">
        <Checkbox checked={selected.has(p.pessoa_id)} onCheckedChange={() => toggleSelect(p.pessoa_id)} data-testid={`select-pessoa-${p.pessoa_id}`} className="flex-shrink-0 hidden sm:flex" />
        <PessoaAvatar pessoa={p} testId={`pessoa-list-photo-${p.pessoa_id}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <p className="font-semibold text-sm truncate cursor-pointer hover:underline" style={{ color: '#0f172a' }} onClick={() => openEdit(p)}>{p.nome_completo}</p>
            <Badge variant="outline" className="text-xs flex-shrink-0">{calcAge(p.data_nascimento)} anos</Badge>
            {p.apelido && <Badge variant="outline" className="text-xs flex-shrink-0 text-amber-700 gap-1" style={{ borderColor: '#b45309' }}><UserRound className="w-3 h-3" />{p.apelido}</Badge>}
            {p.auxiliar_jovens && <Badge variant="outline" className="text-xs flex-shrink-0 text-green-700" style={{ borderColor: '#15803d' }}>Auxiliar</Badge>}
            {p.musico && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-xs flex-shrink-0 text-purple-700 cursor-default" style={{ borderColor: '#7e22ce' }}>Músico</Badge>
                  </TooltipTrigger>
                  <TooltipContent><p>{p.instrumentos?.length ? p.instrumentos.join(', ') : 'Sem instrumentos'}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {p.organista && <Badge variant="outline" className="text-xs flex-shrink-0 text-pink-700" style={{ borderColor: '#be185d' }}>Organista</Badge>}
            {!hasPersonAddress(p) && <TooltipProvider delayDuration={200}><Tooltip><TooltipTrigger asChild><span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-50 text-red-600" data-testid={`pessoa-missing-address-${p.pessoa_id}`}><AlertTriangle className="h-3.5 w-3.5" /></span></TooltipTrigger><TooltipContent><p>Endereço não informado</p></TooltipContent></Tooltip></TooltipProvider>}
          </div>
          <div className="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>Frequência 90 dias:</span>
              {(() => { const eng = getEngagementDisplay(p.frequencia_pct, engajamentoDisplay); return eng.isPercent ? (<><div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(p.frequencia_pct, 100)}%`, background: eng.color }}></div></div><span className="font-semibold" style={{ color: eng.color }}>{eng.label}</span></>) : (<TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${eng.bg}`}>{eng.label}</span></TooltipTrigger><TooltipContent><p className="text-xs">{p.frequencia_pct}%</p></TooltipContent></Tooltip></TooltipProvider>); })()}
            </span>
          </div>
          <div className="flex sm:hidden items-center gap-1.5 text-xs text-slate-500 mt-0.5">
            {(() => { const eng = getEngagementDisplay(p.frequencia_pct, engajamentoDisplay); return eng.isPercent ? (<><div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(p.frequencia_pct, 100)}%`, background: eng.color }}></div></div><span className="font-semibold" style={{ color: eng.color }}>{eng.label}</span></>) : (<TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${eng.bg}`}>{eng.label}</span></TooltipTrigger><TooltipContent><p className="text-xs">{p.frequencia_pct}%</p></TooltipContent></Tooltip></TooltipProvider>); })()}
          </div>
        </div>
        <div className="flex gap-0.5 flex-shrink-0">
          {p.auxiliar_jovens && <Button variant="ghost" size="icon" className="h-8 w-8 text-violet-600 hover:bg-violet-50" onClick={() => handleImpersonate(p)} data-testid={`impersonate-pessoa-${p.pessoa_id}`} title="Ver como auxiliar"><Eye className="w-4 h-4" /></Button>}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-indigo-600 hover:bg-indigo-50" onClick={() => openVisitaFromPessoa(p)} data-testid={`visita-pessoa-${p.pessoa_id}`} title="Solicitar visita"><Home className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:bg-amber-50" onClick={() => openAddAnot(p)} data-testid={`add-anot-pessoa-${p.pessoa_id}`} title="Nova anotacao"><StickyNote className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(p)} data-testid={`edit-pessoa-${p.pessoa_id}`}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-orange-600 hover:bg-orange-50" onClick={() => openInativar(p)} data-testid={`inativar-pessoa-${p.pessoa_id}`} title="Inativar"><UserX className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>;
  };

  const renderSystemUserPessoa = (p) => (
    <div key={p.pessoa_id} className="px-3 sm:px-4 py-4 border-b last:border-b-0 border-slate-100" data-testid={`system-user-pessoa-${p.pessoa_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm text-slate-900 truncate">{p.nome_completo}</p>
            <Badge className={p.usuario_sistema_ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{p.usuario_sistema_ativo ? 'Ativo' : 'Inativo'}</Badge>
            {p.password_status && <Badge variant="outline" className="text-[10px]">{p.password_status === 'configurada' ? 'Senha configurada' : p.password_status === 'troca_obrigatoria' ? 'Troca pendente' : 'Sem senha'}</Badge>}
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600">
            <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />Login: {p.email || 'sem e-mail'}</span>
            <span className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" />Telefone: {p.whatsapp || 'sem telefone'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 rounded-full bg-slate-50 px-3 py-2">
            <span className="text-xs font-medium text-slate-600">Acesso</span>
            <Switch checked={!!p.usuario_sistema_ativo} onCheckedChange={(checked) => handleToggleSystemUser(!!checked, p)} data-testid={`system-user-toggle-${p.pessoa_id}`} />
          </div>
          <Button size="sm" variant="outline" disabled={!p.usuario_sistema_ativo || sendingPasswordId === p.pessoa_id} onClick={() => handleSendTempPassword(p.pessoa_id)} data-testid={`system-user-send-temp-password-${p.pessoa_id}`}>
            {sendingPasswordId === p.pessoa_id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1.5" />}Enviar senha SMS
          </Button>
        </div>
      </div>
    </div>
  );

  // Sidebar items (structured)
  const topItems = [
    { key: 'todos', label: 'Todos', icon: 'bg-blue-100', iconColor: 'text-blue-600' },
  ];
  const gatilhoItems = [];
  const bottomItems = [
    { key: 'usuarios_sistema', label: 'Usuários', icon: 'bg-emerald-100', iconColor: 'text-emerald-600' },
    { key: 'incompletos', label: 'Incompletos', icon: 'bg-purple-100', iconColor: 'text-purple-600' },
    { key: 'pendentes', label: 'Pendentes', icon: 'bg-amber-100', iconColor: 'text-amber-600' },
    { key: 'inativos', label: 'Inativos', icon: 'bg-orange-100', iconColor: 'text-orange-600' },
  ];

  const renderSidebarItem = (item) => (
    <button key={item.key} onClick={() => { setFilter(item.key); clearSelection(); }} data-testid={`filtro-pessoa-${item.key}`}
      className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === item.key ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
      <div className="flex items-center gap-3">
        {item.cor ? (
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.cor}20` }}>
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.cor }}></div>
          </div>
        ) : (
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.icon}`}>
            {item.key === 'inativos' ? <UserMinus className={`w-4 h-4 ${item.iconColor}`} /> : item.key === 'usuarios_sistema' ? <KeyRound className={`w-4 h-4 ${item.iconColor}`} /> : item.key === 'automatico' ? <Zap className={`w-4 h-4 ${item.iconColor}`} /> : item.key === 'pendentes' ? <Clock className={`w-4 h-4 ${item.iconColor}`} /> : item.key === 'incompletos' ? <AlertTriangle className={`w-4 h-4 ${item.iconColor}`} /> : item.key === 'manual' ? <Hand className={`w-4 h-4 ${item.iconColor}`} /> : <ListFilter className={`w-4 h-4 ${item.iconColor}`} />}
          </div>
        )}
        <div>
          <p className={`font-semibold text-sm ${filter === item.key ? 'text-blue-700' : 'text-slate-900'}`}>{item.label}</p>
          {item.desc && <p className="text-[11px] text-slate-400 leading-tight">{item.desc}</p>}
        </div>
      </div>
      <Badge className={`text-xs font-semibold ${filter === item.key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{allCounts[item.key] || 0}</Badge>
    </button>
  );

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <UserRound className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Pessoas</h2>
                    <Badge className="bg-blue-100 text-blue-700 text-sm font-bold" data-testid="pessoas-count">{pessoasStats.length}</Badge>
                  </div>
                  <p className="text-sm text-slate-500">Gerir pessoas</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="pessoas-menu">
                {topItems.map(renderSidebarItem)}
                {bottomItems.map(renderSidebarItem)}
                <div className="pt-2">
                  <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-3 mb-1.5">Gatilhos</p>
                  <div className="space-y-2">
                    {gatilhoItems.map(renderSidebarItem)}
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Button size="sm" onClick={openAdd} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="add-pessoa-btn">
                  <Plus className="w-4 h-4 mr-1" />Nova Pessoa
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setTemplateDialog(true)} className="flex-1 text-slate-500 hover:text-blue-600" title="Baixar modelo Excel" data-testid="download-template-btn"><Download className="w-4 h-4 mr-1" />Modelo</Button>
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing} className="flex-1" data-testid="import-pessoas-btn"><Upload className="w-4 h-4 mr-1" />{importing ? '...' : 'Importar'}</Button>
                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
                </div>
                <Button variant="outline" size="sm" onClick={downloadContactsVcf} className="w-full justify-center text-slate-600 hover:text-blue-600" data-testid="download-contacts-vcf-btn">
                  <Download className="w-4 h-4 mr-1" />Download VCF/vCard
                </Button>
                <Button variant="outline" size="sm" onClick={openBirthdayCalendarDialog} className="w-full justify-center text-slate-600 hover:text-blue-600" data-testid="birthday-calendar-link-btn">
                  <Calendar className="w-4 h-4 mr-1" />Agenda de Aniversários
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <UserRound className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{activeFilterLabel}</h2>
                    <p className="text-sm text-slate-500">{filter === 'pendentes' ? (pendentesSubFilter === 'atualizacoes' ? filteredUpdates.length : filteredPendentes.length) : filter === 'usuarios_sistema' ? usuariosSistema.length : filter === 'incompletos' ? incompletos.length : filtered.length} {(filter === 'pendentes' ? (pendentesSubFilter === 'atualizacoes' ? filteredUpdates.length : filteredPendentes.length) : filter === 'usuarios_sistema' ? usuariosSistema.length : filter === 'incompletos' ? incompletos.length : filtered.length) === 1 ? 'registro' : 'registros'}</p>
                  </div>
                </div>
              </div>
              <div className={`${filter === 'pendentes' ? 'flex flex-col sm:flex-row sm:items-center gap-3' : 'space-y-3'} mt-3`}>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 bg-slate-50 border-slate-200" data-testid="pessoas-search" />
                  </div>
                  {filter !== 'pendentes' && (
                    <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-9 gap-1.5 ${showFilters || filterGenero || filterIdadeMin || filterIdadeMax || filterInfos.length > 0 ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-testid="toggle-filters-btn">
                      <ListFilter className="w-3.5 h-3.5" />
                      Filtros
                      {(filterGenero || filterIdadeMin || filterIdadeMax || filterInfos.length > 0) && <span className="bg-blue-600 text-white rounded-full text-[10px] w-4 h-4 flex items-center justify-center">{(filterGenero ? 1 : 0) + (filterIdadeMin || filterIdadeMax ? 1 : 0) + filterInfos.length}</span>}
                    </Button>
                  )}
                </div>
                {filter !== 'pendentes' && showFilters && (
                  <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-3 mt-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Gênero</p>
                        <Select value={filterGenero || 'all'} onValueChange={(v) => setFilterGenero(v === 'all' ? '' : v)} data-testid="filter-genero">
                          <SelectTrigger className="h-9 bg-white border-slate-200 text-sm" data-testid="filter-genero-trigger">
                            <SelectValue placeholder="Todos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="masculino">Masculino</SelectItem>
                            <SelectItem value="feminino">Feminino</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Idade</p>
                        <div className="flex items-center gap-1">
                          <Input type="number" placeholder="Min" value={filterIdadeMin} onChange={(e) => setFilterIdadeMin(e.target.value)} className="h-9 bg-white border-slate-200 text-sm text-center" data-testid="filter-idade-min" />
                          <span className="text-xs text-slate-400">a</span>
                          <Input type="number" placeholder="Max" value={filterIdadeMax} onChange={(e) => setFilterIdadeMax(e.target.value)} className="h-9 bg-white border-slate-200 text-sm text-center" data-testid="filter-idade-max" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Informações Adicionais</p>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { key: 'batizado', label: 'Batizado' },
                          { key: 'selado', label: 'Selado' },
                          { key: 'auxiliar_jovens', label: 'Auxiliar' },
                          { key: 'grupo_visita', label: 'Grupo Visita' },
                          { key: 'musico', label: 'Músico' },
                          { key: 'organista', label: 'Organista' },
                          { key: 'instrutor_gem', label: 'Instrutor GEM' },
                          { key: 'aluno_gem', label: 'Aluno GEM' },
                          { key: 'instrutora_musicalizacao', label: 'Musicalização' },
                          { key: 'integrante_ebi', label: 'EBI' },
                        ].map(opt => (
                          <button key={opt.key} type="button" onClick={() => setFilterInfos(prev => prev.includes(opt.key) ? prev.filter(f => f !== opt.key) : [...prev, opt.key])}
                            className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors ${filterInfos.includes(opt.key) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}
                            data-testid={`filter-info-${opt.key}`}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {filter === 'pendentes' && (
                  <div className="flex bg-slate-100 rounded-lg p-0.5 flex-shrink-0 w-full sm:w-auto">
                    <button onClick={() => setPendentesSubFilter('pendente')} data-testid="sub-filtro-pendente"
                      className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${pendentesSubFilter === 'pendente' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-900'}`}>
                      Pendentes
                    </button>
                    <button onClick={() => setPendentesSubFilter('atualizacoes')} data-testid="sub-filtro-atualizacoes"
                      className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-colors relative ${pendentesSubFilter === 'atualizacoes' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-900'}`}>
                      Atualizações
                      {pendingUpdates.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white rounded-full text-[10px] flex items-center justify-center">{pendingUpdates.length}</span>}
                    </button>
                    <button onClick={() => setPendentesSubFilter('aprovado')} data-testid="sub-filtro-aprovado"
                      className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${pendentesSubFilter === 'aprovado' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-900'}`}>
                      Aprovados
                    </button>
                    <button onClick={() => setPendentesSubFilter('rejeitado')} data-testid="sub-filtro-rejeitado"
                      className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${pendentesSubFilter === 'rejeitado' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-900'}`}>
                      Reprovados
                    </button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500" data-testid="pessoas-loading-state"><Loader2 className="mb-3 h-8 w-8 animate-spin text-blue-600" /><p className="text-sm font-semibold">Carregando pessoas...</p></div>
              ) : filter === 'pendentes' ? (
                <div data-testid="pendentes-section">
                  {pendentesSubFilter === 'atualizacoes' ? (
                    /* Pending Updates List */
                    <div className="space-y-0">
                      {filteredUpdates.length === 0 ? (
                        <div className="text-center py-12">
                          <RefreshCw className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500 text-sm">{search ? 'Nenhum resultado encontrado' : 'Nenhuma atualização pendente'}</p>
                        </div>
                      ) : <>{visibleUpdates.map(u => (
                        <div key={u.update_id} className="px-3 sm:px-4 py-3 border-b last:border-b-0 border-slate-100" data-testid={`pending-update-${u.update_id}`}>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <PessoaAvatar pessoa={{ ...(u.dados_novos || {}), nome_completo: u.dados_novos?.nome_completo || 'Sem nome' }} fallbackClassName="bg-gradient-to-br from-blue-400 to-blue-600" testId={`pending-update-photo-${u.update_id}`} />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm truncate" style={{ color: '#0f172a' }}>{u.dados_novos?.nome_completo || 'Sem nome'}</p>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                                <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">Atualização</Badge>
                                {u.created_at && <span>{new Date(u.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>}
                              </div>
                            </div>
                            <div className="flex gap-0.5 flex-shrink-0">
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => setUpdateDetailModal(u)} data-testid={`view-update-${u.update_id}`} title="Ver alterações"><Eye className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setConfirmAction({ type: 'reject_update', update: u })} data-testid={`reject-update-${u.update_id}`} title="Reprovar"><X className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => setConfirmAction({ type: 'approve_update', update: u })} data-testid={`approve-update-${u.update_id}`} title="Aprovar"><Check className="w-4 h-4" /></Button>
                            </div>
                          </div>
                        </div>
                      ))}<LoadMoreButton total={filteredUpdates.length} testId="pending-updates" /></>}
                    </div>
                  ) : (
                    /* Regular Pendentes List */
                    <div className="space-y-0">
                      {filteredPendentes.length === 0 ? (
                        <div className="text-center py-12">
                          {pendentesSubFilter === 'pendente' ? <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" /> : pendentesSubFilter === 'aprovado' ? <CheckCircle2 className="w-10 h-10 text-slate-300 mx-auto mb-3" /> : <XCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />}
                          <p className="text-slate-500 text-sm">{search ? 'Nenhum resultado encontrado' : `Nenhum cadastro ${pendentesSubLabel.toLowerCase()}`}</p>
                        </div>
                      ) : <>{visiblePendentes.map(person => { const p = withPhotoOverride(person); return (
                        <div key={p.pessoa_id} className="px-3 sm:px-4 py-3 border-b last:border-b-0 border-slate-100" data-testid={`pendente-pessoa-${p.pessoa_id}`}>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <PessoaAvatar pessoa={p} fallbackClassName={pendentesSubFilter === 'aprovado' ? 'bg-gradient-to-br from-green-500 to-green-600' : pendentesSubFilter === 'rejeitado' ? 'bg-gradient-to-br from-red-400 to-red-500' : 'bg-gradient-to-br from-amber-500 to-amber-600'} testId={`pendente-pessoa-photo-${p.pessoa_id}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                <p className="font-semibold text-sm truncate cursor-pointer hover:underline" style={{ color: '#0f172a' }} onClick={() => openEdit(p)}>{p.nome_completo}</p>
                                {p.data_nascimento && <Badge variant="outline" className="text-xs flex-shrink-0">{calcAge(p.data_nascimento)}</Badge>}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                                {p.genero && <span className="capitalize">{p.genero}</span>}
                                {p.whatsapp && <span>{p.whatsapp}</span>}
                              </div>
                            </div>
                            <div className="flex gap-0.5 flex-shrink-0">
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => setInfoPessoa(p)} data-testid={`info-pessoa-${p.pessoa_id}`} title="Detalhes"><Eye className="w-4 h-4" /></Button>
                              {pendentesSubFilter === 'pendente' && (
                                <>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setConfirmAction({ type: 'reject', pessoa: p })} data-testid={`reject-pessoa-${p.pessoa_id}`} title="Reprovar"><X className="w-4 h-4" /></Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => setConfirmAction({ type: 'approve', pessoa: p })} data-testid={`approve-pessoa-${p.pessoa_id}`} title="Aprovar"><Check className="w-4 h-4" /></Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ); })}<LoadMoreButton total={filteredPendentes.length} testId="pendentes" /></>}
                    </div>
                  )}
                </div>
              ) : filter === 'inativos' ? (
                <div className="space-y-0" data-testid="inativos-section">
                  {inativosLoading ? <p className="text-sm text-slate-400 px-4 py-8 text-center">Carregando...</p>
                  : inativos.length === 0 ? (
                    <div className="text-center py-12">
                      <UserMinus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Nenhuma pessoa inativa</p>
                    </div>
                  ) : <>{visibleInativos.map(person => { const p = withPhotoOverride(person); return (
                    <div key={p.pessoa_id} className="px-3 sm:px-4 py-3 border-b last:border-b-0 border-slate-100" data-testid={`inativo-${p.pessoa_id}`}>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <PessoaAvatar pessoa={p} fallbackClassName="bg-gradient-to-br from-slate-400 to-slate-500 opacity-60" testId={`inativo-pessoa-photo-${p.pessoa_id}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-slate-500 truncate">{p.nome_completo}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5 flex-wrap">
                            <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">{p.motivo_inativacao || 'Sem motivo'}</Badge>
                            {p.inativado_em && <span>em {new Date(p.inativado_em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>}
                          </div>
                        </div>
                        <div className="flex gap-0.5 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => handleReativar(p.pessoa_id)} className="text-green-600 hover:bg-green-50" data-testid={`reativar-${p.pessoa_id}`} title="Reativar">
                            <RotateCcw className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Reativar</span>
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeletePessoa(p.pessoa_id, p.nome_completo)} className="text-red-600 hover:bg-red-50" data-testid={`excluir-${p.pessoa_id}`} title="Excluir">
                            <Trash2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Excluir</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ); })}<LoadMoreButton total={inativos.length} testId="inativos" /></>}
                </div>
              ) : filter === 'usuarios_sistema' ? (
                <div className="space-y-0" data-testid="usuarios-sistema-section">
                  {usuariosSistema.length === 0 ? (
                    <div className="text-center py-12">
                      <KeyRound className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Nenhuma pessoa encontrada</p>
                    </div>
                  ) : <>{visibleUsuariosSistema.map(renderSystemUserPessoa)}<LoadMoreButton total={usuariosSistema.length} testId="usuarios-sistema" /></>}
                </div>
              ) : filter === 'incompletos' ? (
                <div className="space-y-0" data-testid="incompletos-section">
                  {incompletos.length === 0 ? (
                    <div className="text-center py-12">
                      <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Nenhuma pessoa com cadastro incompleto</p>
                    </div>
                  ) : <>{visibleIncompletos.map(person => { const p = withPhotoOverride(person); return (
                    <div key={p.pessoa_id} className="px-3 sm:px-4 py-3 border-b last:border-b-0 border-slate-100" data-testid={`incompleto-${p.pessoa_id}`}>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <PessoaAvatar pessoa={p} fallbackClassName={p.genero === 'masculino' ? 'bg-gradient-to-br from-blue-400 to-blue-500' : 'bg-gradient-to-br from-pink-400 to-pink-500'} testId={`incompleto-pessoa-photo-${p.pessoa_id}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-slate-900 truncate">{p.nome_completo}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5 flex-wrap">
                            {p.whatsapp && <span>{p.whatsapp}</span>}
                            <Badge className="bg-purple-50 text-purple-600 text-[9px]">Precisa revisar todos os dados</Badge>
                          </div>
                        </div>
                        <div className="flex gap-0.5 flex-shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(p)} title="Editar"><Edit className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </div>
                  ); })}<LoadMoreButton total={incompletos.length} testId="incompletos" /></>}
                </div>
              ) : (
              <>
              {selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 mb-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="bulk-action-bar">
                  <Checkbox checked={selected.size === filtered.length} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                  <span className="text-sm font-semibold text-blue-700" data-testid="selected-count">{selected.size} selecionado{selected.size > 1 ? 's' : ''}</span>
                  <Button size="sm" variant="ghost" onClick={clearSelection} className="text-slate-500" data-testid="bulk-cancel-btn"><X className="w-4 h-4" /></Button>
                </div>
              )}
              <div className="space-y-0" data-testid="pessoas-list">
                {visibleFiltered.map(renderPessoa)}
                {filtered.length === 0 && (
                  <div className="text-center py-12">
                    <UserRound className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">{search ? 'Nenhum resultado encontrado' : 'Nenhuma pessoa encontrada'}</p>
                  </div>
                )}
                <LoadMoreButton total={filtered.length} testId="pessoas" />
              </div>
              </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* MAIN EDIT/ADD DIALOG with sidebar */}
      <Dialog open={dialog} onOpenChange={(o) => { if (!o) closeDialog(); else setDialog(o); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] p-0 sm:p-0 overflow-hidden">
          <div className="flex h-[70vh]">
            {/* Sidebar */}
            <div className="w-12 sm:w-56 bg-slate-50 border-r border-slate-200 p-2 sm:p-4 flex flex-col gap-1 flex-shrink-0">
              <p className="hidden sm:block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">{editing ? 'Editar Pessoa' : 'Nova Pessoa'}</p>
              <button onClick={() => setEditTab('info')} data-testid="pessoa-tab-info" className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${editTab === 'info' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`}>
                <FileText className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Informações</span>
              </button>
              {editing && (
                <button onClick={() => setEditTab('anotacoes')} data-testid="pessoa-tab-anotacoes" className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left relative ${editTab === 'anotacoes' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`}>
                  <StickyNote className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Anotações</span>
                  {anotacoes.length > 0 && <span className="sm:ml-auto text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full absolute -top-1 -right-1 sm:static">{anotacoes.length}</span>}
                </button>
              )}
              {editing && form.auxiliar_jovens && (
                <button onClick={() => setEditTab('sobcuidado')} data-testid="pessoa-tab-sobcuidado" className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${editTab === 'sobcuidado' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`}>
                  <Users className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Sob Cuidado</span>
                </button>
              )}
              {editing && (
                <button onClick={() => setEditTab('usuario_sistema')} data-testid="pessoa-tab-usuario-sistema" className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${editTab === 'usuario_sistema' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`}>
                  <KeyRound className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Usuário Sistema</span>
                </button>
              )}
              {editing && (
                <button onClick={() => setEditTab('historico')} data-testid="pessoa-tab-historico" className={`flex items-center justify-center sm:justify-start gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${editTab === 'historico' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-200'}`}>
                  <History className="w-4 h-4 flex-shrink-0" /><span className="hidden sm:inline">Histórico</span>
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-6 sm:pt-8">
              {editTab === 'info' && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Informações</h3>
                  <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-start" data-testid="pessoa-photo-section">
                    <PessoaAvatar pessoa={form} className="h-28 w-28 self-center sm:h-36 sm:w-36 sm:self-start" fallbackClassName="bg-gradient-to-br from-blue-500 to-blue-600 text-4xl border-4 border-white" testId={form.foto_url ? 'pessoa-photo-preview' : 'pessoa-photo-avatar'} preferThumb={false} />
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap justify-end gap-2" data-testid="pessoa-photo-actions">
                        {form.foto_url && <Button type="button" size="icon" variant="ghost" onClick={handleRemovePhoto} className="h-8 w-8 text-red-600 hover:bg-red-50" data-testid="pessoa-photo-remove-button" title="Excluir foto"><X className="w-4 h-4" /></Button>}
                        <Button type="button" size="icon" variant="outline" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} data-testid="pessoa-photo-upload-button" title={form.foto_url ? 'Trocar foto' : 'Enviar foto'}>
                          {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        </Button>
                      <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoUpload} className="hidden" data-testid="pessoa-photo-file-input" />
                      </div>
                      {form.foto_url && <div className="mb-2 inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-600" data-testid="pessoa-photo-size-badge">Otimizada: {formatPhotoSize(form.foto_size)}</div>}
                      {form.foto_url && <PhotoFrameControls form={form} setForm={setForm} onAutoSave={schedulePhotoFrameAutoSave} prefix="pessoa" />}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="col-span-1 sm:col-span-2 flex gap-3">
                      <div className="flex-[7]"><Label>Nome Completo *</Label><Input value={form.nome_completo} onChange={(e) => setForm({ ...form, nome_completo: e.target.value })} className={errors.nome_completo ? 'border-red-500' : ''} data-testid="input-nome-completo" />{errors.nome_completo && <p className="text-xs text-red-500 mt-1">{errors.nome_completo}</p>}</div>
                      <div className="flex-[3]"><Label>Apelido</Label><Input value={form.apelido || ''} onChange={(e) => setForm({ ...form, apelido: e.target.value })} data-testid="input-apelido" /></div>
                    </div>
                    <div>
                      <Label>Gênero *</Label>
                      <Select value={form.genero} onValueChange={(v) => setForm(prev => ({ ...prev, genero: v, organista: false, instrutora_musicalizacao: false, integrante_ebi: false, musico: false, instrumentos: [], instrutor_gem: false, aluno_gem: false }))}>
                        <SelectTrigger className={errors.genero ? 'border-red-500' : ''}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="masculino">Masculino</SelectItem><SelectItem value="feminino">Feminino</SelectItem></SelectContent>
                      </Select>
                      {errors.genero && <p className="text-xs text-red-500 mt-1">{errors.genero}</p>}
                    </div>
                    <div><Label>Data Nascimento *</Label><Input type="date" value={form.data_nascimento} onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} className={errors.data_nascimento ? 'border-red-500' : ''} />{errors.data_nascimento && <p className="text-xs text-red-500 mt-1">{errors.data_nascimento}</p>}</div>
                    <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={errors.email ? 'border-red-500' : ''} />{errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}</div>
                    <div><Label>WhatsApp</Label><Input data-testid="pessoa-form-whatsapp-input" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: formatPhone(e.target.value) })} className={errors.whatsapp ? 'border-red-500' : ''} />{errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}</div>
                    <div><Label>Nome do Pai *</Label><Input value={form.nome_pai} onChange={(e) => setForm({ ...form, nome_pai: e.target.value })} className={errors.nome_pai ? 'border-red-500' : ''} />{errors.nome_pai && <p className="text-xs text-red-500 mt-1">{errors.nome_pai}</p>}</div>
                    <div><Label>WhatsApp Pai</Label><Input value={form.whatsapp_pai} onChange={(e) => setForm({ ...form, whatsapp_pai: formatPhone(e.target.value) })} /></div>
                    <div><Label>Nome da Mae *</Label><Input value={form.nome_mae} onChange={(e) => setForm({ ...form, nome_mae: e.target.value })} className={errors.nome_mae ? 'border-red-500' : ''} />{errors.nome_mae && <p className="text-xs text-red-500 mt-1">{errors.nome_mae}</p>}</div>
                    <div><Label>WhatsApp Mae</Label><Input value={form.whatsapp_mae} onChange={(e) => setForm({ ...form, whatsapp_mae: formatPhone(e.target.value) })} /></div>
                    <PersonAddressFields form={form} setForm={setForm} prefix="pessoa-form" />
                  </div>
                  <div className="pt-3 flex flex-col gap-2">
                    <p className="text-sm font-semibold text-slate-700">Informações adicionais</p>
                    <div className="flex items-center space-x-2"><Checkbox checked={form.batizado} onCheckedChange={(c) => setForm({ ...form, batizado: !!c })} id="p-bat" /><Label htmlFor="p-bat" className="cursor-pointer">E batizado?</Label></div>
                    <div className="flex items-center space-x-2"><Checkbox checked={form.selado} onCheckedChange={(c) => setForm({ ...form, selado: !!c })} id="p-sel" /><Label htmlFor="p-sel" className="cursor-pointer">E selado?</Label></div>
                    <div className="flex items-center space-x-2"><Checkbox checked={form.auxiliar_jovens} onCheckedChange={(c) => setForm({ ...form, auxiliar_jovens: !!c })} id="p-axj" /><Label htmlFor="p-axj" className="cursor-pointer">E auxiliar de jovens?</Label></div>
                    <div className="flex items-center space-x-2"><Checkbox checked={form.grupo_visita} onCheckedChange={(c) => setForm({ ...form, grupo_visita: !!c })} id="p-gv" /><Label htmlFor="p-gv" className="cursor-pointer">É integrante de um dos Grupos de Visita?</Label></div>
                    {form.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={form.organista} onCheckedChange={(c) => setForm({ ...form, organista: !!c })} id="p-org" /><Label htmlFor="p-org" className="cursor-pointer">É organista?</Label></div>}
                    {form.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={form.instrutora_musicalizacao} onCheckedChange={(c) => setForm({ ...form, instrutora_musicalizacao: !!c })} id="p-im" /><Label htmlFor="p-im" className="cursor-pointer">É instrutora/auxiliar da aula de musicalização?</Label></div>}
                    {form.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={form.integrante_ebi} onCheckedChange={(c) => setForm({ ...form, integrante_ebi: !!c })} id="p-ebi" /><Label htmlFor="p-ebi" className="cursor-pointer">É integrante do EBI (cuida das crianças)?</Label></div>}
                    {form.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={form.instrutor_gem} onCheckedChange={(c) => setForm({ ...form, instrutor_gem: !!c })} id="p-igem" /><Label htmlFor="p-igem" className="cursor-pointer">É instrutor de música (GEM)?</Label></div>}
                    {form.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={form.aluno_gem} onCheckedChange={(c) => setForm({ ...form, aluno_gem: !!c })} id="p-agem" /><Label htmlFor="p-agem" className="cursor-pointer">É aluno da aula de música (GEM)?</Label></div>}
                    {form.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={form.musico} onCheckedChange={(c) => setForm({ ...form, musico: !!c })} id="p-mus" /><Label htmlFor="p-mus" className="cursor-pointer">É músico?</Label></div>}
                    {form.genero === 'masculino' && form.musico && (
                      <div className="ml-6 p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-sm text-slate-600 mb-2">Selecione os instrumentos:</p>
                        <div className="grid grid-cols-2 grid-flow-col grid-rows-12 gap-2">
                          {INSTRUMENTOS.map((inst) => (
                            <div key={inst} className="flex items-center space-x-2">
                              <Checkbox checked={form.instrumentos.includes(inst)} onCheckedChange={() => toggleInstrumento(inst)} id={`inst-${inst}`} />
                              <Label htmlFor={`inst-${inst}`} className="cursor-pointer text-sm">{inst}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <Button onClick={handleSave} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 mt-4">{saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aguarde...</> : 'Salvar'}</Button>
                </div>
              )}

              {editTab === 'anotacoes' && editing && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Anotações</h3>
                    <Button size="sm" onClick={() => { resetAnotForm(); setAnotDialog(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-anotacao-btn"><Plus className="w-4 h-4 mr-1" />Nova Anotação</Button>
                  </div>
                  <p className="text-sm text-slate-500">Acontecimentos importantes na vida do jovem.</p>
                  <div data-testid="anotacoes-list">
                    {anotLoading ? (
                      <div className="text-center py-8 text-slate-400"><p className="text-sm">Carregando...</p></div>
                    ) : anotacoes.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <StickyNote className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Nenhuma anotação registrada</p>
                      </div>
                    ) : anotacoes.map(a => (
                      <div key={a.anotacao_id} className="flex items-center justify-between px-4 py-3 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`anotacao-${a.anotacao_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><StickyNote className="w-4 h-4 text-amber-600" /></div>
                          <div className="min-w-0">
                            <p className="text-sm text-slate-900">{a.descricao}</p>
                            {a.data && <span className="flex items-center gap-1 text-xs text-slate-500 mt-0.5"><Calendar className="w-3 h-3" />{fmtDate(a.data)}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => { setEditingAnot(a); setAnotForm({ titulo: '', descricao: a.descricao, data: a.data || '' }); setAnotDialog(true); }} className="text-blue-600 hover:bg-blue-50" data-testid={`edit-anotacao-${a.anotacao_id}`}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAnot(a.anotacao_id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-anotacao-${a.anotacao_id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {editTab === 'historico' && (
                <HistoricoTab pessoaId={editing?.pessoa_id} />
              )}

              {editTab === 'usuario_sistema' && editing && (
                <div className="space-y-4" data-testid="pessoa-usuario-sistema-panel">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Usuário do Sistema</h3>
                    <p className="text-sm text-slate-500">Controle de acesso por e-mail e senha temporária enviada por SMS.</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Acesso ao sistema</p>
                        <p className="text-xs text-slate-500">Quando inativo, a pessoa não consegue fazer login.</p>
                      </div>
                      <Switch checked={!!form.usuario_sistema_ativo} onCheckedChange={(checked) => handleToggleSystemUser(!!checked)} disabled={saving} data-testid="pessoa-usuario-sistema-toggle" />
                    </div>
                    {form.usuario_sistema_ativo && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label>Login</Label>
                          <Input value={form.email || ''} readOnly data-testid="pessoa-usuario-sistema-email" className="bg-white" />
                        </div>
                        <div>
                          <Label>Telefone para SMS</Label>
                          <Input value={form.whatsapp || ''} readOnly data-testid="pessoa-usuario-sistema-phone" className="bg-white" />
                        </div>
                        <div className="sm:col-span-2">
                          <Button onClick={() => handleSendTempPassword()} disabled={sendingPasswordId === editing.pessoa_id || saving} data-testid="pessoa-send-temp-password-button" className="w-full bg-blue-600 hover:bg-blue-700">
                            {sendingPasswordId === editing.pessoa_id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1.5" />}Enviar senha temporária por SMS
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {editTab === 'sobcuidado' && editing && (() => {
                const assignedFuncs = serviceFunctions.filter(f => f.tipo === 'recitativo' && f.active !== false && (f.assigned_people || []).includes(editing.pessoa_id));
                return (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Pessoas sob Cuidado</h3>
                    {assignedFuncs.length === 0 ? (
                      <div className="text-center py-10 text-slate-400">
                        <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Não atribuída a nenhuma fila de recitativo.</p>
                        <p className="text-xs mt-1">Atribua em Configurações &gt; Escalas &gt; Recitativos.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {assignedFuncs.map(func => {
                          const idadeMin = func.eligibility?.idade_min || 0;
                          const idadeMax = func.eligibility?.idade_max || 99;
                          const matchPeople = pessoas.filter(p => {
                            if (p.pessoa_id === editing.pessoa_id) return false;
                            const age = calcAge(p.data_nascimento);
                            if (age === null || age === undefined) return false;
                            const genderMatch = !func.eligibility?.gender || func.eligibility.gender === 'todos' || (p.genero || '').toLowerCase() === func.eligibility.gender;
                            return age >= idadeMin && age <= idadeMax && genderMatch;
                          });
                          return (
                            <div key={func.function_id} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                              <div className="flex items-center gap-2 mb-3">
                                <p className="text-sm font-semibold text-slate-800">{func.nome}</p>
                                <Badge variant="outline" className="text-[10px]">{idadeMin}–{idadeMax} anos</Badge>
                                <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-200">{matchPeople.length} pessoa{matchPeople.length !== 1 ? 's' : ''}</Badge>
                              </div>
                              {/* Co-auxiliares na mesma fila */}
                              {(() => {
                                const coAux = (func.assigned_people || []).filter(pid => pid !== editing.pessoa_id);
                                if (coAux.length === 0) return null;
                                return (
                                  <div className="mb-3 flex items-center gap-1.5 flex-wrap">
                                    <span className="text-[11px] text-slate-500">Junto com:</span>
                                    {coAux.map(pid => {
                                      const p = pessoas.find(x => x.pessoa_id === pid);
                                      return <span key={pid} className="text-[11px] font-medium text-slate-700">{p ? (p.apelido || (p.nome_completo || '').split(' ')[0]) : '?'}</span>;
                                    })}
                                  </div>
                                );
                              })()}
                              {matchPeople.length === 0 ? (
                                <p className="text-xs text-slate-400 italic">Nenhuma pessoa nesta faixa etária.</p>
                              ) : (
                                <div className="space-y-1.5">
                                  {matchPeople.sort((a, b) => (a.nome_completo || '').localeCompare(b.nome_completo || '')).map(p => (
                                    <div key={p.pessoa_id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-100">
                                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${(func.eligibility?.gender === 'feminino') ? 'bg-pink-100' : (func.eligibility?.gender === 'masculino') ? 'bg-blue-100' : 'bg-slate-100'}`}>
                                        <span className={`text-xs font-bold ${(func.eligibility?.gender === 'feminino') ? 'text-pink-600' : (func.eligibility?.gender === 'masculino') ? 'text-blue-600' : 'text-slate-600'}`}>{(p.nome_completo || '?')[0]}</span>
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-slate-800 truncate">
                                          {p.nome_completo}
                                          <span className="text-xs text-slate-400 font-normal ml-1.5">{calcAge(p.data_nascimento)} anos</span>
                                          {p.apelido && <span className="text-xs text-slate-400 font-normal ml-1">· {p.apelido}</span>}
                                        </p>
                                      </div>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0" onClick={() => setQuickViewPessoa(p)} data-testid={`sobcuidado-open-${p.pessoa_id}`}>
                                        <ChevronRight className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick View Modal — Pessoa sob cuidado */}
      <Dialog open={!!quickViewPessoa} onOpenChange={(v) => { if (!v) setQuickViewPessoa(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">{quickViewPessoa?.nome_completo}</DialogTitle>
          </DialogHeader>
          {quickViewPessoa && (() => {
            const p = quickViewPessoa;
            const age = calcAge(p.data_nascimento);
            return (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {p.apelido && <><span className="text-slate-500">Apelido</span><span className="font-medium text-slate-800">{p.apelido}</span></>}
                  <span className="text-slate-500">Idade</span><span className="font-medium text-slate-800">{age} anos</span>
                  <span className="text-slate-500">Género</span><span className="font-medium text-slate-800 capitalize">{p.genero}</span>
                  <span className="text-slate-500">Nascimento</span><span className="font-medium text-slate-800">{p.data_nascimento ? formatBrazilDate(p.data_nascimento, '-') : '-'}</span>
                  {p.whatsapp && <><span className="text-slate-500">WhatsApp</span><span className="font-medium text-slate-800">{p.whatsapp}</span></>}
                  {p.nome_pai && p.nome_pai !== 'Nao informado' && <><span className="text-slate-500">Pai</span><span className="font-medium text-slate-800">{p.nome_pai}</span></>}
                  {p.whatsapp_pai && <><span className="text-slate-500">WhatsApp Pai</span><span className="font-medium text-slate-800">{p.whatsapp_pai}</span></>}
                  {p.nome_mae && p.nome_mae !== 'Nao informado' && <><span className="text-slate-500">Mãe</span><span className="font-medium text-slate-800">{p.nome_mae}</span></>}
                  {p.whatsapp_mae && <><span className="text-slate-500">WhatsApp Mãe</span><span className="font-medium text-slate-800">{p.whatsapp_mae}</span></>}
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {p.batizado && <Badge variant="outline" className="text-[10px]">Batizado</Badge>}
                  {p.selado && <Badge variant="outline" className="text-[10px]">Selado</Badge>}
                  {p.musico && <Badge variant="outline" className="text-[10px]">Músico</Badge>}
                  {p.organista && <Badge variant="outline" className="text-[10px]">Organista</Badge>}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={anotDialog} onOpenChange={(v) => { setAnotDialog(v); if (!v) resetAnotForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingAnot ? 'Editar Anotação' : 'Nova Anotação'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Data</Label>
              <Input type="date" value={anotForm.data} onChange={(e) => setAnotForm({ ...anotForm, data: e.target.value })} data-testid="anotacao-data" />
              <p className="text-xs text-slate-500 mt-1">Opcional. Data do acontecimento.</p>
            </div>
            <div>
              <Label>Descrição *</Label>
              <textarea value={anotForm.descricao} onChange={(e) => setAnotForm({ ...anotForm, descricao: e.target.value })} rows={4} className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none" data-testid="anotacao-descricao" />
            </div>
            <Button onClick={handleSaveAnot} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="anotacao-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aguarde...</> : editingAnot ? 'Atualizar' : 'Salvar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* INATIVAR DIALOG */}
      <Dialog open={inativarDialog} onOpenChange={(v) => { setInativarDialog(v); if (!v) setInativarPessoa(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Inativar Pessoa</DialogTitle></DialogHeader>
          {inativarPessoa && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Tem certeza que deseja inativar <strong>{inativarPessoa.nome_completo}</strong>? A pessoa não aparecerá mais nas listagens, mas os dados serão preservados.</p>
              <div>
                <Label>Motivo da inativacao *</Label>
                <Select value={inativarMotivo} onValueChange={setInativarMotivo}>
                  <SelectTrigger data-testid="inativar-motivo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {motivos.length > 0 ? motivos.map(m => (
                      <SelectItem key={m.motivo_id} value={m.nome}>{m.nome}</SelectItem>
                    )) : (
                      <SelectItem value="Outro motivo">Outro motivo</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleInativar} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="confirmar-inativar-btn">
                <UserX className="w-4 h-4 mr-2" />Confirmar Inativação
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM DIALOG */}
      <Dialog open={deleteConfirmDialog} onOpenChange={setDeleteConfirmDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Confirmar Exclusão</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Tem certeza que deseja excluir <strong>{deleteTarget?.nome}</strong>?</p>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmDialog(false)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDeletePessoa} data-testid="confirm-delete-pessoa-btn">Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Info/Details Modal for Pendentes */}
      <Dialog open={!!infoPessoa} onOpenChange={() => setInfoPessoa(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Detalhes do Cadastro</DialogTitle></DialogHeader>
          {infoPessoa && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                {infoPessoa.foto_url
                  ? <img src={`${BACKEND_URL}${infoPessoa.foto_url}`} alt="" className="w-12 h-12 rounded-full object-cover" />
                  : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">{infoPessoa.nome_completo.charAt(0)}</div>
                }
                <div>
                  <p className="font-semibold text-slate-900">{infoPessoa.nome_completo}</p>
                  {infoPessoa.data_nascimento && <p className="text-sm text-slate-500">{calcAge(infoPessoa.data_nascimento)}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {infoPessoa.data_nascimento && <div><p className="text-slate-400 text-xs">Nascimento</p><p className="text-slate-900">{fmtDate(infoPessoa.data_nascimento)}</p></div>}
                {infoPessoa.genero && <div><p className="text-slate-400 text-xs">Gênero</p><p className="text-slate-900">{infoPessoa.genero === 'masculino' ? 'Masculino' : 'Feminino'}</p></div>}
                <div><p className="text-slate-400 text-xs">Batizado</p><p className="text-slate-900">{infoPessoa.batizado ? 'Sim' : 'Não'}</p></div>
                <div><p className="text-slate-400 text-xs">Selado</p><p className="text-slate-900">{infoPessoa.selado ? 'Sim' : 'Não'}</p></div>
                {infoPessoa.whatsapp && <div><p className="text-slate-400 text-xs">WhatsApp</p><p className="text-slate-900">{infoPessoa.whatsapp}</p></div>}
                {infoPessoa.email && <div className="col-span-2"><p className="text-slate-400 text-xs">Email</p><p className="text-slate-900">{infoPessoa.email}</p></div>}
                {infoPessoa.nome_pai && <div><p className="text-slate-400 text-xs">Nome do Pai</p><p className="text-slate-900">{infoPessoa.nome_pai}</p></div>}
                {infoPessoa.whatsapp_pai && <div><p className="text-slate-400 text-xs">WhatsApp Pai</p><p className="text-slate-900">{infoPessoa.whatsapp_pai}</p></div>}
                {infoPessoa.nome_mae && <div><p className="text-slate-400 text-xs">Nome da Mãe</p><p className="text-slate-900">{infoPessoa.nome_mae}</p></div>}
                {infoPessoa.whatsapp_mae && <div><p className="text-slate-400 text-xs">WhatsApp Mãe</p><p className="text-slate-900">{infoPessoa.whatsapp_mae}</p></div>}
                {infoPessoa.responsavel && <div><p className="text-slate-400 text-xs">Responsável</p><p className="text-slate-900">{infoPessoa.responsavel}</p></div>}
                {infoPessoa.whatsapp_responsavel && <div><p className="text-slate-400 text-xs">WhatsApp Responsável</p><p className="text-slate-900">{infoPessoa.whatsapp_responsavel}</p></div>}
                {infoPessoa.endereco && <div className="col-span-2"><p className="text-slate-400 text-xs">Endereço</p><p className="text-slate-900">{infoPessoa.endereco}</p></div>}
                {infoPessoa.observacoes && <div className="col-span-2"><p className="text-slate-400 text-xs">Observações</p><p className="text-slate-900">{infoPessoa.observacoes}</p></div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Approve/Reject Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === 'approve' ? 'Aprovar Cadastro' : confirmAction?.type === 'reject' ? 'Reprovar Cadastro' : confirmAction?.type === 'approve_update' ? 'Aprovar Atualização' : 'Reprovar Atualização'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {confirmAction?.type?.includes('update')
              ? <>Deseja realmente {confirmAction?.type === 'approve_update' ? 'aprovar' : 'reprovar'} a atualização de <span className="font-semibold">{confirmAction?.update?.dados_novos?.nome_completo}</span>?</>
              : <>Deseja realmente {confirmAction?.type === 'approve' ? 'aprovar' : 'reprovar'} o cadastro de <span className="font-semibold">{confirmAction?.pessoa?.nome_completo}</span>?</>
            }
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmAction(null)} data-testid="confirm-pendente-cancel">Cancelar</Button>
            <Button size="sm" className={confirmAction?.type?.includes('approve') ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} onClick={() => {
              if (confirmAction.type === 'approve') onApprove(confirmAction.pessoa.pessoa_id);
              else if (confirmAction.type === 'reject') onReject(confirmAction.pessoa.pessoa_id);
              else if (confirmAction.type === 'approve_update') onApproveUpdate(confirmAction.update.update_id);
              else if (confirmAction.type === 'reject_update') onRejectUpdate(confirmAction.update.update_id);
              setConfirmAction(null);
            }} data-testid="confirm-pendente-action">
              {confirmAction?.type?.includes('approve') ? 'Aprovar' : 'Reprovar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Detail Modal */}
      <Dialog open={!!updateDetailModal} onOpenChange={() => setUpdateDetailModal(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Atualização</DialogTitle>
          </DialogHeader>
          {updateDetailModal && (() => {
            const old = updateDetailModal.dados_antigos || {};
            const novo = updateDetailModal.dados_novos || {};
            const fields = [
              { key: 'nome_completo', label: 'Nome' },
              { key: 'genero', label: 'Gênero' },
              { key: 'data_nascimento', label: 'Data Nascimento' },
              { key: 'email', label: 'E-mail' },
              { key: 'whatsapp', label: 'WhatsApp' },
              { key: 'whatsapp_pai', label: 'WhatsApp Pai' },
              { key: 'whatsapp_mae', label: 'WhatsApp Mãe' },
              { key: 'nome_pai', label: 'Nome Pai' },
              { key: 'nome_mae', label: 'Nome Mãe' },
              { key: 'batizado', label: 'Batizado' },
              { key: 'selado', label: 'Selado' },
              { key: 'auxiliar_jovens', label: 'Auxiliar Jovens' },
              { key: 'grupo_visita', label: 'Grupo Visita' },
              { key: 'organista', label: 'Organista' },
              { key: 'musico', label: 'Músico' },
              { key: 'instrumentos', label: 'Instrumentos' },
            ];
            const changes = fields.filter(f => {
              const o = Array.isArray(old[f.key]) ? old[f.key].join(', ') : String(old[f.key] ?? '');
              const n = Array.isArray(novo[f.key]) ? novo[f.key].join(', ') : String(novo[f.key] ?? '');
              return o !== n;
            });
            const fmt = (v) => {
              if (v === true) return 'Sim';
              if (v === false) return 'Não';
              if (Array.isArray(v)) return v.join(', ') || '-';
              return v || '-';
            };
            return (
              <div className="space-y-4">
                <p className="text-sm text-slate-500">Pessoa: <span className="font-semibold text-slate-800">{novo.nome_completo}</span></p>
                {changes.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Nenhuma alteração detectada.</p>
                ) : (
                  <div className="space-y-2">
                    {changes.map(f => (
                      <div key={f.key} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-xs font-semibold text-slate-400 uppercase mb-1">{f.label}</p>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-red-500 line-through">{fmt(old[f.key])}</span>
                          <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <span className="text-green-600 font-medium">{fmt(novo[f.key])}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setUpdateDetailModal(null)}>Fechar</Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => { setConfirmAction({ type: 'reject_update', update: updateDetailModal }); setUpdateDetailModal(null); }}>Reprovar</Button>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { setConfirmAction({ type: 'approve_update', update: updateDetailModal }); setUpdateDetailModal(null); }}>Aprovar</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Template Instructions Modal */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Como montar o modelo para importacao</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-slate-700">
            <p className="text-slate-500">Monte um arquivo <strong>.xlsx</strong> (Excel) seguindo as colunas abaixo. A primeira linha deve conter os cabecalhos.</p>
            <div className="space-y-2">
              <p className="font-semibold text-slate-900">Colunas obrigatórias:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Nome Completo</strong> - Texto</li>
                <li><strong>Gênero</strong> - <code className="bg-slate-100 px-1 rounded text-xs">masculino</code> ou <code className="bg-slate-100 px-1 rounded text-xs">feminino</code></li>
                <li><strong>Data Nascimento</strong> - Formato <code className="bg-slate-100 px-1 rounded text-xs">DD/MM/AAAA</code></li>
                <li><strong>Nome do Pai</strong> - Texto</li>
                <li><strong>Nome da Mae</strong> - Texto</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-slate-900">Colunas opcionais:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Email</strong> - Texto</li>
                <li><strong>WhatsApp</strong> - Ex: <code className="bg-slate-100 px-1 rounded text-xs">(11) 99999-9999</code></li>
                <li><strong>Batizado, Selado, Auxiliar de Jovens, Grupo de Visita</strong> - <code className="bg-slate-100 px-1 rounded text-xs">sim</code> ou <code className="bg-slate-100 px-1 rounded text-xs">nao</code></li>
                <li><strong>Organista, Instrutora Musicalização, Integrante EBI</strong> - Apenas feminino</li>
                <li><strong>Músico, Instrutor GEM, Aluno GEM</strong> - Apenas masculino</li>
                <li><strong>Instrumentos</strong> - Separados por virgula. Ex: <code className="bg-slate-100 px-1 rounded text-xs">Violao, Flauta</code></li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-800 text-xs"><strong>Dica:</strong> Baixe o modelo pronto com o cabecalho e um exemplo preenchido para facilitar.</p>
            </div>
            <Button onClick={() => { downloadTemplate(); setTemplateDialog(false); }} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="download-template-confirm-btn">
              <Download className="w-4 h-4 mr-2" />Baixar Modelo Excel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={birthdayCalendarDialog} onOpenChange={setBirthdayCalendarDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Agenda de Aniversários</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Copie este link e adicione no Google Agenda em <strong>Outras agendas → Adicionar por URL</strong>.</p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">A descrição dos eventos inclui as informações completas do cadastro da pessoa.</p>
            <div>
              <Label>Link da agenda</Label>
              <div className="mt-1 flex gap-2">
                <Input value={birthdayCalendarLoading ? 'Gerando link...' : birthdayCalendarUrl} readOnly className="font-mono text-xs" data-testid="birthday-calendar-url-input" />
                <Button onClick={copyBirthdayCalendarUrl} disabled={!birthdayCalendarUrl || birthdayCalendarLoading} data-testid="copy-birthday-calendar-url-btn">Copiar</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={visitaOpen} onOpenChange={(o) => { if (!o) { setVisitaForm({ nome: '', telefone: '', observacao: '' }); setVisitaMatched(null); setVisitaPhoneChecked(false); setVisitaPhoneChecking(false); } setVisitaOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="visita-from-pessoa-modal">
          <DialogHeader><DialogTitle>Nova Solicitação de Visita</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateVisita} className="space-y-3">
            <div>
              <Label className="text-sm">WhatsApp *</Label>
              <Input value={visitaForm.telefone} onChange={e => visitaHandlePhoneChange(e.target.value)} data-testid="visita-pessoa-telefone" className="mt-1" />
            </div>
            {visitaPhoneChecking && (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-1"><Loader2 className="w-4 h-4 animate-spin" /><span>Buscando pessoa...</span></div>
            )}
            {visitaPhoneChecked && visitaMatched && (
              <div className="mt-3" data-testid="visita-pessoa-cards">
                {(visitaMatched.pessoas || [visitaMatched]).map((p, i) => (
                  <div key={i} className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3 flex items-center gap-3 mb-3">
                    <PessoaAvatar pessoa={p} className="w-11 h-11" fallbackClassName="bg-indigo-600 text-base" testId={`visita-matched-pessoa-photo-${p.pessoa_id || i}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{p.nome_completo}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {p.genero && <Badge className="text-[10px] bg-indigo-100 text-indigo-700">{p.genero}</Badge>}
                        {p.idade != null && <span className="text-xs text-slate-500">{p.idade} anos</span>}
                      </div>
                      {p.via && <p className="text-[11px] text-slate-500 mt-1">{p.via === 'Relacionada' ? 'Pessoa relacionada' : `Este é o WhatsApp ${p.via === 'Próprio' ? 'da própria pessoa' : p.via === 'Mãe' ? 'da Mãe' : 'do Pai'}`}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {visitaPhoneChecked && !visitaMatched && !visitaPhoneChecking && (
              <div><Label className="text-sm">Nome *</Label><Input value={visitaForm.nome} onChange={e => setVisitaForm({ ...visitaForm, nome: e.target.value })} data-testid="visita-pessoa-nome" className="mt-1" autoFocus /></div>
            )}
            <div>
              <Label className="text-sm">Descrição</Label>
              <Textarea value={visitaForm.observacao} onChange={e => setVisitaForm({ ...visitaForm, observacao: e.target.value })} rows={3} maxLength={2000} data-testid="visita-pessoa-obs" className="mt-1 resize-none" />
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={visitaLoading || visitaPhoneChecking} data-testid="submit-visita-pessoa">
              {visitaLoading ? 'Salvando...' : 'Cadastrar Solicitação'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
